/**
 * Near-me Telegram consent fields — gated on linked chat id (not phone).
 * Default off until link + explicit opt-in (parent D6).
 * Exception (connect-adoption D5/D8): onboarding may auto-enable once after a successful bot Start.
 */
import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/lib/database.types";

export type NearMeTelegramConsentSnapshot = {
  enabled: boolean;
  opt_in_at: string | null;
  opt_out_at: string | null;
};

export type NearMeTelegramConsentPatch = {
  near_me_notify_telegram: boolean;
  near_me_telegram_opt_in_at: string | null;
  near_me_telegram_opt_out_at: string | null;
};

export function isTelegramChatIdLinked(
  chatId: string | null | undefined,
): boolean {
  return Boolean(chatId?.trim());
}

/**
 * Computes next DB values for Telegram near-me consent.
 * Cannot enable without a linked chat id; unlinking while enabled stamps opt-out.
 */
export function nextNearMeTelegramConsentFields(args: {
  nowIso: string;
  previous: NearMeTelegramConsentSnapshot;
  requestedEnabled: boolean;
  telegramChatId: string | null;
}): NearMeTelegramConsentPatch {
  const linked = isTelegramChatIdLinked(args.telegramChatId);
  const want = args.requestedEnabled && linked;

  if (!linked && args.previous.enabled) {
    return {
      near_me_notify_telegram: false,
      near_me_telegram_opt_in_at: args.previous.opt_in_at,
      near_me_telegram_opt_out_at: args.nowIso,
    };
  }
  if (!linked) {
    return {
      near_me_notify_telegram: false,
      near_me_telegram_opt_in_at: args.previous.opt_in_at,
      near_me_telegram_opt_out_at: args.previous.opt_out_at,
    };
  }

  if (want && !args.previous.enabled) {
    return {
      near_me_notify_telegram: true,
      near_me_telegram_opt_in_at: args.nowIso,
      near_me_telegram_opt_out_at: null,
    };
  }
  if (!want && args.previous.enabled) {
    return {
      near_me_notify_telegram: false,
      near_me_telegram_opt_in_at: args.previous.opt_in_at,
      near_me_telegram_opt_out_at: args.nowIso,
    };
  }

  return {
    near_me_notify_telegram: args.previous.enabled,
    near_me_telegram_opt_in_at: args.previous.opt_in_at,
    near_me_telegram_opt_out_at: args.previous.opt_out_at,
  };
}

/** /stop or disconnect: disable Telegram near-me if currently on. */
export async function disableNearMeTelegramOptIn(
  client: SupabaseClient<Database>,
  userId: string,
  nowIso = new Date().toISOString(),
): Promise<void> {
  const { data, error } = await client
    .from("notification_preferences")
    .select("near_me_notify_telegram, near_me_telegram_opt_in_at")
    .eq("user_id", userId)
    .maybeSingle();

  if (error) {
    console.error("[near_me_telegram] prefs read for disable:", error);
    return;
  }

  if (!data?.near_me_notify_telegram) return;

  const { error: upErr } = await client.from("notification_preferences").upsert(
    {
      user_id: userId,
      near_me_notify_telegram: false,
      near_me_telegram_opt_in_at: data.near_me_telegram_opt_in_at ?? null,
      near_me_telegram_opt_out_at: nowIso,
      updated_at: nowIso,
    },
    { onConflict: "user_id" },
  );

  if (upErr) {
    console.error("[near_me_telegram] prefs disable:", upErr);
  }
}

/**
 * Enable Telegram near-me after a successful bot link (onboarding / homepage auto path).
 * Still requires a linked chat id; uses the same consent stamp fields as the toggle.
 */
export async function enableNearMeTelegramOptInAfterLink(
  client: SupabaseClient<Database>,
  params: {
    userId: string;
    telegramChatId: string;
    previous: NearMeTelegramConsentSnapshot;
    nowIso?: string;
  },
): Promise<
  { ok: true; patch: NearMeTelegramConsentPatch } | { ok: false; error: string }
> {
  const nowIso = params.nowIso ?? new Date().toISOString();
  const patch = nextNearMeTelegramConsentFields({
    nowIso,
    previous: params.previous,
    requestedEnabled: true,
    telegramChatId: params.telegramChatId,
  });

  if (!patch.near_me_notify_telegram) {
    return { ok: false, error: "not_linked_or_already_off_path" };
  }

  const { error: upErr } = await client.from("notification_preferences").upsert(
    {
      user_id: params.userId,
      near_me_notify_telegram: patch.near_me_notify_telegram,
      near_me_telegram_opt_in_at: patch.near_me_telegram_opt_in_at,
      near_me_telegram_opt_out_at: patch.near_me_telegram_opt_out_at,
      updated_at: nowIso,
    },
    { onConflict: "user_id" },
  );

  if (upErr) {
    console.error("[near_me_telegram] prefs auto opt-in:", upErr);
    return { ok: false, error: upErr.message };
  }

  return { ok: true, patch };
}
